import Genders from '@/common/components/targeting/Genders';
import { AudienceRuleModes, isExternalSegmentRule } from '@/common/enums/audience-rule-modes';
import { DataProviderCodes } from '@/common/enums/data-provider-codes';
import DemographicCategory from '@/common/enums/DemographicCategory.enum';
import DemographicType from '@/common/enums/DemographicType.enum';
import { ExternalSegmentBackendTypes } from '@/common/enums/external-segments/external-segment-backend-types.enum';
import { ExternalSegmentTypes } from '@/common/enums/external-segments/external-segment-types.enum';
import { DataProvider } from '@/common/models';
import AudienceLookBackWindow from '@/common/models/audience/AudienceLookBackWindow';
import AudienceRule from '@/common/models/audience/AudienceRule';
import AudienceRuleCustomGroup from '@/common/models/audience/AudienceRuleCustomGroup';
import ExternalSegmentType from '@/common/models/audience/ExternalSegmentType';
import Demographic from '@/common/models/demographics/Demographic';
import { Filter, FilterComparisonOperator } from '@/common/models/query-params';
import { DASH, first, groupBy, isValidURL, last, orderBy, uniq } from '@/globals';
import PrizmData from '@/org-app/components/audienceLogics/data/prizm/PrizmData';
import CountryCodes from '@/utils/CountryCodes';
import QuickMathUtils from '@/utils/QuickMathUtils';
import i18n from '@/vueI18n';

const grouping = {
  [ExternalSegmentTypes.BRAND]: [
    ExternalSegmentBackendTypes.BRAND,
    ExternalSegmentBackendTypes.ONLINE_BRAND,
    ExternalSegmentBackendTypes.BRAND_AFFINITIES,
  ],
  [ExternalSegmentTypes.PLACE_VISITOR]: [
    ExternalSegmentBackendTypes.CATEGORY,
    ExternalSegmentBackendTypes.PLACE_VISITS,
  ],
  [ExternalSegmentTypes.LIFESTYLE]: [ExternalSegmentBackendTypes.GEO_BEHAVIOR, ExternalSegmentBackendTypes.EVENTS],
  [ExternalSegmentTypes.INTEREST]: [ExternalSegmentBackendTypes.IAB_CATEGORY, ExternalSegmentBackendTypes.INTERESTS],
  [ExternalSegmentTypes.INTENT]: [ExternalSegmentBackendTypes.INTENT],
  [ExternalSegmentTypes.APP_USAGE]: [
    ExternalSegmentBackendTypes.DEVICE_CARRIER,
    ExternalSegmentBackendTypes.APP_CATEGORY,
    ExternalSegmentBackendTypes.APP_USAGE,
  ],
  [ExternalSegmentTypes.AUTO]: [ExternalSegmentBackendTypes.AUTO, ExternalSegmentBackendTypes.AUTOMOTIVE_AND_CARS],
  [ExternalSegmentTypes.BANKING_INVESTING]: [ExternalSegmentBackendTypes.BANKING_INVESTING],
  [ExternalSegmentTypes.BOOKS_MAGAZINES]: [ExternalSegmentBackendTypes.BOOKS_MAGAZINES],
  [ExternalSegmentTypes.CHARITABLE_DONORS]: [ExternalSegmentBackendTypes.CHARITABLE_DONORS],
  [ExternalSegmentTypes.CPG]: [ExternalSegmentBackendTypes.CPG],
  [ExternalSegmentTypes.CREDIT_DEBIT_CARDS]: [ExternalSegmentBackendTypes.CREDIT_DEBIT_CARDS],
  [ExternalSegmentTypes.DEMOGRAPHICS]: [ExternalSegmentBackendTypes.DEMOGRAPHICS],
  [ExternalSegmentTypes.FINANCIALS]: [
    ExternalSegmentBackendTypes.FINANCIALS,
    ExternalSegmentBackendTypes.FINANCIAL_SERVICES,
    ExternalSegmentBackendTypes.SPENDING_BEHAVIOR,
  ],
  [ExternalSegmentTypes.FOOD_COOKING]: [ExternalSegmentBackendTypes.FOOD_COOKING],
  [ExternalSegmentTypes.FOOD_DRINK]: [
    ExternalSegmentBackendTypes.FOOD_DRINK,
    ExternalSegmentBackendTypes.EATING_PLACES,
  ],
  [ExternalSegmentTypes.HEALTHCARE]: [ExternalSegmentBackendTypes.HEALTHCARE],
  [ExternalSegmentTypes.HOME_REAL_ESTATE]: [ExternalSegmentBackendTypes.HOME_REAL_ESTATE],
  [ExternalSegmentTypes.HOUSEHOLD]: [
    ExternalSegmentBackendTypes.HOUSEHOLD,
    ExternalSegmentBackendTypes.HOME_IMPROVEMENT_FURNISHINGS,
  ],
  [ExternalSegmentTypes.INSURANCE]: [ExternalSegmentBackendTypes.INSURANCE],
  [ExternalSegmentTypes.LIFESTYLES_INTERESTS]: [ExternalSegmentBackendTypes.LIFESTYLES_INTERESTS],
  [ExternalSegmentTypes.PURCHASE_BEHAVIORS]: [ExternalSegmentBackendTypes.PURCHASE_BEHAVIORS],
  [ExternalSegmentTypes.PURCHASE_HISTORY]: [ExternalSegmentBackendTypes.PURCHASE_HISTORY],
  [ExternalSegmentTypes.RETAIL]: [
    ExternalSegmentBackendTypes.RETAIL,
    ExternalSegmentBackendTypes.RETAIL_SHOPPING,
    ExternalSegmentBackendTypes.SPECIALTY_RETAIL,
    ExternalSegmentBackendTypes.GENERAL_MERCHANDISE_STORES,
    ExternalSegmentBackendTypes.GROCERY_DRUG_CONVENIENCE_STORES,
    ExternalSegmentBackendTypes.SMALL_BUSINESS,
  ],
  [ExternalSegmentTypes.SPORTS]: [ExternalSegmentBackendTypes.SPORTS],
  [ExternalSegmentTypes.TECHNOLOGY_CONNECTIVITY]: [
    ExternalSegmentBackendTypes.TECHNOLOGY_CONNECTIVITY,
    ExternalSegmentBackendTypes.TECHNOLOGY,
    ExternalSegmentBackendTypes.SMARTPHONE,
    ExternalSegmentBackendTypes.TELECOMMUNICATIONS_CABLE,
  ],
  [ExternalSegmentTypes.TRAVEL]: [ExternalSegmentBackendTypes.TRAVEL, ExternalSegmentBackendTypes.TRAVEL_TOURISM],
  [ExternalSegmentTypes.POLITICAL]: [ExternalSegmentBackendTypes.POLITICAL],
  [ExternalSegmentTypes.TRANSACTIONAL]: [ExternalSegmentBackendTypes.TRANSACTIONAL],
  [ExternalSegmentTypes.SEASONAL]: [ExternalSegmentBackendTypes.SEASONAL],
  [ExternalSegmentTypes.APPAREL]: [ExternalSegmentBackendTypes.APPAREL],
  [ExternalSegmentTypes.EDUCATION]: [ExternalSegmentBackendTypes.EDUCATION],
  [ExternalSegmentTypes.WELLNESS]: [ExternalSegmentBackendTypes.WELLNESS, ExternalSegmentBackendTypes.PERSONAS],
  [ExternalSegmentTypes.SERVICES]: [ExternalSegmentBackendTypes.SERVICES],
  [ExternalSegmentTypes.ENTERTAINMENT]: [
    ExternalSegmentBackendTypes.ENTERTAINMENT,
    ExternalSegmentBackendTypes.ART_MEDIA,
  ],
};

const ageDemos = [
  new Demographic(3, '18-24', DemographicType.Age, null, 'E 18-24', false),
  new Demographic(4, '25-34', DemographicType.Age, null, 'E 25-34', false),
  new Demographic(5, '35-44', DemographicType.Age, null, 'E 35-44', false),
  new Demographic(7, '45-54', DemographicType.Age, null, 'E 45-54', false),
  new Demographic(8, '55-64', DemographicType.Age, null, 'E 55-64', false),
  new Demographic(9, '65+', DemographicType.Age, null, '65+', false),
];

const prizmId = 'PZML';

const audienceSegmentTypes = () => {
  const types = [];
  Object.keys(grouping).forEach((key) => {
    types.push(
      ExternalSegmentType.fromObject({
        id: key,
        grouping: grouping[key],
        segments: [],
      }),
    );
  });
  return types;
};

const audienceSegmentTypeBEToFE = (segment) => {
  return Object.values(ExternalSegmentTypes).find((segmentType) => {
    return grouping[segmentType]?.includes(segment);
  });
};

const enabledFilter = new Filter('enabled', [true], FilterComparisonOperator.EQUAL);

const getSegmentIdFromS3Path = (s3Path, isEpsilon = false) => {
  const params = s3Path.split('/');
  const segmentIdParam = params.find((p) => p.toLowerCase().includes('segmentid'));
  const start = segmentIdParam.indexOf('=') + 1;
  const end = segmentIdParam.length + 1;
  return isEpsilon ? segmentIdParam.slice(start, end).replace('-', '=') : segmentIdParam.slice(start, end);
};

const getSegmentIDFromCustomGroup = (customGroup: AudienceRuleCustomGroup, dataProvider?: DataProvider) => {
  if (customGroup.externalSegmentID) {
    return customGroup.externalSegmentID;
  }
  return getSegmentIdFromS3Path(customGroup.s3Path, dataProvider?.code === DataProviderCodes.EPSILON);
};

const getCountryCodeFromSegmentId = (segmentId) => segmentId.substring(3, 6);

const getGenderFromSegmentId = (segmentId) => {
  const genders = Genders.options();
  const hasMale = segmentId.indexOf(1) > -1;
  const hasFemale = segmentId.indexOf(2) > -1;

  if (hasMale && hasFemale) return genders.find((gender) => gender.id === 'A');
  return genders.find((gender) => (gender.id === hasMale ? 'M' : 'F'));
};

const getAgeRangeFromSegmentId = (segmentId) => {
  const ids = segmentId.substring(6, segmentId.length).split('');
  const ageRanges = [];

  ids.forEach((id) => {
    // 1 and 2 are used to determine gender
    if (id > 2) {
      const ageRange = ageDemos.find((age) => age.id.toString() === id);
      if (ageRange) {
        ageRanges.push(ageRange);
      }
    }
  });

  return ageRanges?.length ? ageRanges : ageDemos;
};

const getHsCategoryByLifesightCategory = (cat) => {
  const types = audienceSegmentTypes();
  const hsCat = types.find((type) => type.grouping.indexOf(cat) > -1);
  return hsCat ? i18n.global.t(`names.${hsCat.id}`, 1) : cat;
};

const getCountryFlag = (countryCode) => {
  if (!CountryCodes[countryCode]) return '';

  const codePoints = CountryCodes[countryCode]
    .substring(0, 2)
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt());

  return String.fromCodePoint(...codePoints);
};

const audienceRulesBeToFe = (audienceLogic) => {
  const keysInLogicalExpression = audienceLogic.logicalExpression.split(/[(,)&|!]+/);
  const audienceRulesInLogicalExpression = audienceLogic.audienceRules.filter(
    (rule) => keysInLogicalExpression.indexOf(rule.key.toString()) > -1,
  );
  const newRules = [];

  audienceRulesInLogicalExpression.forEach((rule) => {
    const isInNewRulesAlready = newRules.find(
      (r) => rule.name === r.name && rule.mode === AudienceRuleModes.PredefinedDemographic,
    );
    if (!isInNewRulesAlready) newRules.push(rule);
  });

  return newRules;
};

const maxAudienceKey = (audienceLogic) => {
  const demoSegmentCount = audienceLogic.audienceRules.filter(
    (r) => r.mode === AudienceRuleModes.PredefinedDemographic && r.segments?.length,
  ).length;
  const newAudienceRulesKeys = audienceLogic?.audienceRules?.length
    ? [...audienceLogic.audienceRules.map((a) => a.key)]
    : [];
  const oldAudienceRulesKeys = audienceLogic?.apiObject?.audiencerules?.length
    ? [...audienceLogic.apiObject.audiencerules.map((a) => a.key)]
    : [];
  const keys = uniq([...newAudienceRulesKeys, ...oldAudienceRulesKeys]);
  return Math.max(...keys) + demoSegmentCount;
};

const demoSegmentsToRules = (demoRule, segments, baseKey, isNegated) => {
  const rules = [];
  let expr = '';

  // Helper to turn segment into rule
  const addDemoRule = (segment, key) => {
    const newRule = AudienceRule.fromObject({
      ...demoRule,
      key: key,
      id: -1,
      mode: AudienceRuleModes.PredefinedDemographic,
      isNegated: isNegated,
      audienceRuleCustomGroup: new AudienceRuleCustomGroup(demoRule.name, segment.s3Path),
      audienceLookbackWindows: demoRule.audienceLookbackWindows
        ? [
            AudienceLookBackWindow.fromObject({
              ...demoRule.audienceLookbackWindows[0],
              id: -1,
              audienceRuleID: null,
            }),
          ]
        : [],
    });
    rules.push(newRule);
  };

  // Go through all segments, construct expression string and construct rule
  segments.forEach((segment, i) => {
    const key = baseKey + i;
    addDemoRule(segment, key);
    if (i === 0) {
      if (isNegated) expr += '!'; // negation if needed
      expr += '('; // start group
    } else {
      expr += '|';
    }
    expr += `${key}`;
    if (i === segments.length - 1) {
      // close group
      expr += ')';
    }
  });

  return [rules, expr];
};

const audienceLogicFeToBe = (audienceLogic) => {
  let logicalExpression = audienceLogic.logicalExpression;
  let audienceRules = [...audienceLogic.audienceRules];

  // get predefined demo rules
  const predefinedDemoRules = audienceRules.filter(
    (rule) => rule.mode === AudienceRuleModes.PredefinedDemographic && rule.segments?.length,
  );
  // rewrite them
  if (predefinedDemoRules.length > 0) {
    // base key for the new rules
    const maxKey = maxAudienceKey(audienceLogic);
    predefinedDemoRules.forEach((demoRule) => {
      const isNegated = demoRule.isNegated;
      const keyStr = `${demoRule.key}`;
      const posInRules = audienceRules.indexOf(demoRule);
      const posInExpr = logicalExpression.indexOf(keyStr);

      // get age & gender segments, we'll make rules out of these
      const genderSegments = orderBy(
        demoRule.segments.filter((segment) => segment.category === DemographicCategory.Gender),
        'name',
      );
      const ageSegments = orderBy(
        demoRule.segments.filter((segment) => segment.category === DemographicCategory.Age),
        'name',
      );

      // Get segment rules and corresponding expression strings
      const [genderRules, genderExpr] = demoSegmentsToRules(demoRule, genderSegments, maxKey, isNegated);
      const [ageRules, ageExpr] = demoSegmentsToRules(demoRule, ageSegments, maxKey + genderSegments.length, isNegated);
      // Splice in those rules in place of the old single demo rule
      audienceRules = [
        ...audienceRules.slice(0, posInRules),
        ...genderRules,
        ...ageRules,
        ...audienceRules.slice(posInRules + 1),
      ];
      // Same splicing in the expression
      logicalExpression =
        logicalExpression.slice(0, posInExpr - (isNegated ? 1 : 0)) +
        `${genderExpr}${genderExpr.length && ageExpr.length ? '&' : ''}${ageExpr}` +
        logicalExpression.slice(posInExpr + keyStr.length);
    });
  }

  return {
    audienceRules,
    logicalExpression,
  };
};

// rewrite the audiencelogic to support demographics rules (1 demographics rule in the frontend = multiple rules in the backend)
const rewriteLogicalExpression = (audienceLogic, newRules) => {
  let logicalExpression = audienceLogic.logicalExpression;
  // Remove missing keys
  const keys = newRules.map((r) => `${r.key}`);
  logicalExpression = logicalExpression.replaceAll(/[|&]?!?(\d+)/g, (str, key) => (keys.includes(key) ? str : ''));
  // Remove groups with just 1 demo rule
  logicalExpression = logicalExpression.replaceAll(/(!?)\((\d+)\)/g, (str, not, key) =>
    newRules.find((r) => r.key === Number(key)).mode === AudienceRuleModes.PredefinedDemographic ? not + key : str,
  );
  // Remove empty groups
  logicalExpression = logicalExpression.replaceAll(/[|&]?!?(\(\)|\(\(\)\))/g, '');

  return logicalExpression;
};

const getSegmentNameForDemoRule = (rule, audienceRules) => {
  if (!rule || !audienceRules?.length) return null;

  let name;
  if (rule.mode === AudienceRuleModes.PredefinedDemographic) {
    const beRule = audienceRules.find((r) => r.name === rule.name);
    const segment = beRule.segments?.find((s) => s.s3Path === rule.audienceRuleCustomGroup.s3Path);
    if (segment) {
      const id = getSegmentIdFromS3Path(segment.s3Path);
      const country = getCountryCodeFromSegmentId(id);
      name = `${country}-${segment.name}`;
    } else {
      name = DASH;
    }
  } else {
    name = rule.name;
  }
  return name;
};

const calculateReachForDemographicsAudience = (segments, genderSegments) => {
  const segmentsByCountry = groupBy(segments, 'country');
  const reach = [];

  Object.keys(segmentsByCountry).forEach((key) => {
    const gender = segmentsByCountry[key].filter((seg) => seg.category === DemographicCategory.Gender);
    const age = segmentsByCountry[key].filter((seg) => seg.category === DemographicCategory.Age);

    const genderSegmentByCountry = gender?.length ? genderSegments.filter((g) => g.country === key) : null;
    const selectedGenderReachTotal = gender?.length ? QuickMathUtils.sumBy(gender, 'reach') : null;
    const genderReachTotal = gender?.length ? QuickMathUtils.sumBy(genderSegmentByCountry, 'reach') : null;
    const genderRatio = gender?.length ? selectedGenderReachTotal / genderReachTotal : null;
    const ageReach = age?.length ? QuickMathUtils.sumBy(age, 'reach') : null;

    if (gender?.length && age?.length) reach.push(Math.round(ageReach * genderRatio));
    if (gender?.length && !age?.length) reach.push(selectedGenderReachTotal);
    if (!gender?.length && age?.length) reach.push(ageReach);
  });

  return QuickMathUtils.sum(reach);
};

const getAgeLiteral = (ageRange) => {
  if (!ageRange?.length || ageRange.length === 6) return i18n.global.t('messages.anyAgeGroups');

  const firstAge = first(ageRange[0].split('-'));
  const lastAge = last(last(ageRange).split('-'));
  const isLastAgeNotARange = lastAge.includes('+');
  const hasOnlyOneAge = ageRange.length === 1;

  return isLastAgeNotARange ? `${firstAge}${hasOnlyOneAge ? '' : '+'}` : `${firstAge}-${lastAge}`;
};

const getDemographicsName = (selectedAgeRange, selectedGender, selectedCountries) => {
  const items = [];

  if (selectedAgeRange) {
    const sortAgeRange = selectedAgeRange.sort();
    const age = getAgeLiteral(sortAgeRange);
    items.push(age);
  }

  if (selectedGender) {
    const gender = selectedGender.name;
    items.push(gender);
  }

  if (!selectedAgeRange && !selectedGender) {
    items.push(i18n.global.t('messages.allDemographics'));
  }

  if (selectedCountries) {
    const countries = orderBy(selectedCountries, 'name')
      .map((c) => c.name)
      .join(', ')
      .replace(/,([^,]*)$/, ' and$1');
    items.push('in');
    items.push(countries);
  }

  return items.join(' ');
};

const isDemographicALifesightPrecrunchedSegment = (demographic) =>
  demographic.type === DemographicType.Custom && !demographic.organizationId && demographic.code?.startsWith('LS');

const isDemographicAnEpsilonSegment = (demographic) =>
  demographic.type === DemographicType.Custom &&
  !demographic.organizationId &&
  (demographic.name?.startsWith('[EP') || demographic.name?.startsWith('[SV'));

const isPrizmSegment = (demographic) =>
  demographic.type === DemographicType.Custom && demographic.code?.includes(prizmId);

const getSortedCustomAudiences = (demographics) =>
  demographics
    .filter(
      (demo) =>
        demo.type === DemographicType.Custom &&
        !isDemographicALifesightPrecrunchedSegment(demo) &&
        !isDemographicAnEpsilonSegment(demo) &&
        !isPrizmSegment(demo),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

const getSortedCustomAudiencesForPublicisProd = (demographics) =>
  demographics
    .filter(
      (demo) =>
        (demo.type === DemographicType.Custom || demo.type === DemographicType.All) &&
        !isDemographicALifesightPrecrunchedSegment(demo) &&
        !isDemographicAnEpsilonSegment(demo) &&
        !isPrizmSegment(demo),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

const getFormattedLifesightDemographicName = (name) => {
  let formattedName = name;
  if (name.startsWith('[LSD')) {
    formattedName = name.split('] ')[1];
  }

  return formattedName;
};

const getPrizmImage = (demographic) => {
  if (!demographic?.code) return null;

  const imageCode = demographic.code.split(prizmId)[1];
  return `/assets/images/prizm/PRIZM-${imageCode}.png`;
};

const getPrizmCode = (demographic) => demographic.code?.split(prizmId)[1];
const getPrizmName = (demographic) => demographic.name.slice(3, demographic.name.length);
const getPrizmDetails = (demographic) => {
  const code = getPrizmCode(demographic);
  return PrizmData.segmentDetails.find((details) => code === details.code);
};

function isAudienceRuleComplete(rule) {
  const s3Path = rule.audienceRuleCustomGroup?.s3Path;
  if (rule.mode === AudienceRuleModes.Geofence) {
    return !!rule.geofenceGroupID;
  } else if (rule.mode === AudienceRuleModes.Demographic) {
    return !!s3Path && (isValidURL(s3Path) || !!s3Path.match('s3://.*.csv'));
  } else if (rule.mode === AudienceRuleModes.PredefinedDemographic || isExternalSegmentRule(rule.mode)) {
    return !!rule.segments?.length;
  } else {
    return true;
  }
}

const AudienceSegmentUtils = {
  ageDemos,
  audienceLogicFeToBe,
  audienceRulesBeToFe,
  audienceSegmentTypes,
  calculateReachForDemographicsAudience,
  enabledFilter,
  getAgeLiteral,
  getAgeRangeFromSegmentId,
  getCountryCodeFromSegmentId,
  getDemographicsName,
  getFormattedLifesightDemographicName,
  getGenderFromSegmentId,
  getSegmentIdFromS3Path,
  getSegmentNameForDemoRule,
  getHsCategoryByLifesightCategory,
  getCountryFlag,
  getPrizmCode,
  getPrizmDetails,
  getPrizmImage,
  getPrizmName,
  getSortedCustomAudiences,
  getSortedCustomAudiencesForPublicisProd,
  grouping,
  isDemographicALifesightPrecrunchedSegment,
  prizmId,
  rewriteLogicalExpression,
  isAudienceRuleComplete,
  maxAudienceKey,
  audienceSegmentTypeBEToFE,
  getSegmentIDFromCustomGroup,
};

export default AudienceSegmentUtils;
